import * as React from "react";
import { Badge, Button, Input } from "reactstrap";

import {
  ContentCategoryRow,
  ContentEvalVote,
  ContentQaCheckCode,
  ContentQuestionRow,
  QA_CHECK_LABELS,
} from "../../../common/contentTypes";
import { ContentTable } from "../ContentTable";
import { categoryLabel } from "../useCategories";

/**
 * Every code, in rubric order, so a row shows what was checked rather than only
 * what went wrong — a question with no chips would otherwise be ambiguous
 * between "clean" and "not checked".
 */
const QA_CODES: ContentQaCheckCode[] = ["C2", "C4", "C7", "C11", "C12", "C13"];

/** Compact tooltips, matching the rubric's own wording. */
const QA_SHORT: Record<ContentQaCheckCode, string> = {
  C2: "Label length within cap",
  C4: "No duplicate choices",
  C7: "Exactly 4 choices",
  C11: "Exactly one correct",
  C12: "Not a bank duplicate",
  C13: "Profanity filter",
};

const DIFFICULTY_COLOR: Record<string, string> = {
  easy: "success",
  medium: "warning",
  hard: "danger",
};

const REVIEW_COLOR: Record<string, string> = {
  approved: "success",
  rejected: "danger",
  pending: "secondary",
};

interface Props {
  questions: ContentQuestionRow[];
  categories: ContentCategoryRow[];
  selected: Set<string>;
  expanded: Set<string>;
  busyId: string | null;
  votes: Record<string, ContentEvalVote>;
  prompt?: string;
  /** A published set is a record of what shipped, so decisions are frozen. */
  readOnly?: boolean;
  onToggleSelected: (questionId: string) => void;
  onToggleAll: () => void;
  onToggleExpanded: (questionId: string) => void;
  onReview: (
    questionIds: string[],
    reviewStatus: "approved" | "rejected",
  ) => void;
  onEdit: (question: ContentQuestionRow) => void;
  onVote: (question: ContentQuestionRow, vote: ContentEvalVote) => void;
}

export const ReviewTable: React.FC<Props> = ({
  questions,
  categories,
  selected,
  expanded,
  busyId,
  votes,
  prompt,
  readOnly,
  onToggleSelected,
  onToggleAll,
  onToggleExpanded,
  onReview,
  onEdit,
  onVote,
}) => {
  const selectable = questions.filter(
    (question) => question.reviewStatus !== "approved",
  );
  const allSelected =
    selectable.length > 0 &&
    selectable.every((question) => selected.has(question.id));

  return (
    <ContentTable className="table table-hover mb-0">
      <thead>
        <tr>
          {readOnly ? null : (
            <th style={{ width: 32 }}>
              <Input
                type="checkbox"
                className="position-static m-0"
                checked={allSelected}
                onChange={onToggleAll}
                title="Select all"
              />
            </th>
          )}
          <th style={{ width: 28 }} />
          <th>Title</th>
          <th style={{ width: 170 }}>Category</th>
          <th style={{ width: 90 }}>Difficulty</th>
          <th style={{ width: 160 }}>Choices</th>
          <th style={{ width: 230 }}>QA</th>
          <th style={{ width: 90 }}>Status</th>
          <th style={{ width: 80 }}>Feedback</th>
          <th style={{ width: 120 }} className="text-right" />
        </tr>
      </thead>
      <tbody>
        {questions.map((question) => {
          const status = question.reviewStatus ?? "pending";
          const busy = busyId === question.id;
          const isOpen = expanded.has(question.id);
          const failed = new Set(
            (question.qa?.checks ?? [])
              .filter((check) => !check.pass)
              .map((check) => check.code),
          );
          const checked = new Set(
            (question.qa?.checks ?? []).map((check) => check.code),
          );
          const correct = question.choices.find((choice) => choice.correct);
          const vote = votes[question.id];

          return (
            <React.Fragment key={question.id}>
              <tr>
                {readOnly ? null : (
                  <td>
                    <Input
                      type="checkbox"
                      className="position-static m-0"
                      checked={selected.has(question.id)}
                      onChange={() => onToggleSelected(question.id)}
                    />
                  </td>
                )}
                <td>
                  <Button
                    color="link"
                    className="p-0"
                    title={isOpen ? "Collapse" : "Show choices"}
                    onClick={() => onToggleExpanded(question.id)}
                  >
                    <i
                      className={`fa fa-fw fa-caret-${
                        isOpen ? "down" : "right"
                      }`}
                    />
                  </Button>
                </td>

                <td>{question.title}</td>

                <td className="small text-muted">
                  {categoryLabel(categories, question.categoryId)}
                </td>

                <td>
                  <Badge color={DIFFICULTY_COLOR[question.difficulty]}>
                    {question.difficulty}
                  </Badge>
                </td>

                <td className="small">
                  {correct && (
                    <>
                      <i className="fas fa-check text-success mr-1" />
                      {correct.label}
                    </>
                  )}
                  {question.choices.length > 1 && (
                    <span className="text-muted">
                      {" "}
                      · +{question.choices.length - 1}
                    </span>
                  )}
                </td>

                <td>
                  {QA_CODES.filter((code) => checked.has(code)).map((code) => (
                    <Badge
                      key={code}
                      color={failed.has(code) ? "warning" : "success"}
                      className="mr-1"
                      title={
                        failed.has(code)
                          ? QA_CHECK_LABELS[code]
                          : `${QA_SHORT[code]} — passed`
                      }
                    >
                      {code} {failed.has(code) ? "✕" : "✓"}
                    </Badge>
                  ))}
                </td>

                <td>
                  <Badge color={REVIEW_COLOR[status]}>{status}</Badge>
                </td>

                <td
                  className="text-nowrap"
                  title="Rate this question — feedback that tunes future generation quality"
                >
                  <Button
                    color="link"
                    className="p-0 mr-2"
                    title="Good question"
                    onClick={() => onVote(question, "up")}
                  >
                    <i
                      className={`${
                        vote === "up" ? "fas text-success" : "far text-muted"
                      } fa-thumbs-up`}
                    />
                  </Button>
                  <Button
                    color="link"
                    className="p-0"
                    title="Poor question"
                    onClick={() => onVote(question, "down")}
                  >
                    <i
                      className={`${
                        vote === "down" ? "fas text-danger" : "far text-muted"
                      } fa-thumbs-down`}
                    />
                  </Button>
                </td>

                <td className="text-right text-nowrap">
                  {readOnly ? null : (
                    <Button
                      size="sm"
                      color="success"
                      outline={status !== "approved"}
                      className="mr-1"
                      disabled={busy || status === "approved"}
                      title={
                        status === "approved" ? "Already approved" : "Approve"
                      }
                      onClick={() => onReview([question.id], "approved")}
                    >
                      <i className="fas fa-check" />
                    </Button>
                  )}
                  {readOnly ? null : (
                    <Button
                      size="sm"
                      color="primary"
                      outline
                      className="mr-1"
                      title="Edit"
                      onClick={() => onEdit(question)}
                    >
                      <i className="fas fa-pen" />
                    </Button>
                  )}
                  {readOnly ? null : (
                    <Button
                      size="sm"
                      color="danger"
                      outline={status !== "rejected"}
                      disabled={busy || status === "rejected"}
                      title={
                        status === "rejected" ? "Already rejected" : "Reject"
                      }
                      onClick={() => onReview([question.id], "rejected")}
                    >
                      <i className="fas fa-times" />
                    </Button>
                  )}
                </td>
              </tr>

              {isOpen && (
                <tr>
                  <td colSpan={10} className="bg-light">
                    <div className="row">
                      {question.choices.map((choice) => (
                        <div className="col-md-6 mb-1" key={choice.id}>
                          {choice.correct ? (
                            <>
                              <i className="fas fa-check text-success mr-2" />
                              <strong>{choice.label}</strong>
                            </>
                          ) : (
                            <span className="ml-4">{choice.label}</span>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="small text-muted mt-2">
                      <em>Factual basis:</em>{" "}
                      {question.source.note ||
                        (prompt
                          ? `Generated from prompt: “${prompt}”`
                          : "Not recorded for this question.")}
                      {question.source.model
                        ? ` · ${question.source.model}`
                        : ""}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </ContentTable>
  );
};
